'use client';

import React, { useEffect, useState, useCallback, useMemo } from 'react';

interface ObfuscatedEmailProps {
    /**
     * The email address to obfuscate (e.g., "sales@active710.com")
     */
    email: string;
    
    /**
     * Optional display text (defaults to email if not provided)
     */
    children?: React.ReactNode;
    
    /**
     * Optional className for styling
     */
    className?: string;
    
    /**
     * Whether to render as plain text instead of a link
     * @default false
     */
    asText?: boolean;
    
    /**
     * Optional aria-label for accessibility
     */
    ariaLabel?: string;
}

/**
 * Encode email to base64 for obfuscation
 * Kept outside component for performance (no re-creation on each render)
 */
const encodeEmail = (emailStr: string): string => {
    if (typeof window === 'undefined') return '';
    return btoa(emailStr);
};

/**
 * Decode email from base64
 * Kept outside component for performance (no re-creation on each render)
 */
const decodeEmail = (encodedStr: string): string => {
    if (typeof window === 'undefined') return '';
    try {
        return atob(encodedStr);
    } catch (e) {
        console.error('Failed to decode email:', e);
        return '';
    }
};

/**
 * ObfuscatedEmail Component
 * 
 * A client-side only component that obfuscates email addresses to prevent
 * spam bots from harvesting them. Uses multiple obfuscation techniques:
 * 
 * 1. Client-side only rendering (no SSR/SSG)
 * 2. Base64 encoding in data attributes
 * 3. Email reconstruction on mount
 * 4. Dynamic href construction
 * 
 * @example
 * // As a link (default)
 * <ObfuscatedEmail email="sales@active710.com" />
 * 
 * @example
 * // With custom display text
 * <ObfuscatedEmail email="sales@active710.com">
 *   Contact our sales team
 * </ObfuscatedEmail>
 * 
 * @example
 * // As plain text
 * <ObfuscatedEmail email="sales@active710.com" asText />
 */
const ObfuscatedEmailComponent: React.FC<ObfuscatedEmailProps> = ({
    email,
    children,
    className,
    asText = false,
    ariaLabel,
}) => {
    const [decodedEmail, setDecodedEmail] = useState<string>('');
    const [isClient, setIsClient] = useState<boolean>(false);

    // Memoize encoded email to avoid recalculation on every render
    const encodedEmail = useMemo(() => encodeEmail(email), [email]);

    // Initialize on client-side only
    useEffect(() => {
        setIsClient(true);
        const decoded = decodeEmail(encodedEmail);
        setDecodedEmail(decoded);
    }, [encodedEmail]);

    // Handle click with additional obfuscation
    // Only recreate when decodedEmail changes
    const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
        // Allow default mailto behavior, but reconstruct URL dynamically
        // This prevents bots from easily parsing the href attribute
        if (decodedEmail) {
            e.preventDefault();
            window.location.href = `mailto:${decodedEmail}`;
        }
    }, [decodedEmail]);

    // Memoize display text to avoid recalculation
    const displayText = useMemo(
        () => children || decodedEmail,
        [children, decodedEmail]
    );

    // Memoize aria label to avoid recalculation
    const ariaLabelText = useMemo(
        () => ariaLabel || `Email ${decodedEmail}`,
        [ariaLabel, decodedEmail]
    );

    // Memoize mailto href to avoid string concatenation on every render
    const mailtoHref = useMemo(
        () => `mailto:${decodedEmail}`,
        [decodedEmail]
    );

    // Return null during SSR to prevent hydration mismatches
    if (!isClient) {
        return null;
    }

    // If no email decoded, show fallback
    if (!decodedEmail) {
        return <span className={className}>{children || 'Contact us'}</span>;
    }

    // Render as plain text
    if (asText) {
        return (
            <span 
                className={className}
                aria-label={ariaLabelText}
            >
                {displayText}
            </span>
        );
    }

    // Render as link
    return (
        <a
            href={mailtoHref}
            className={className}
            onClick={handleClick}
            aria-label={ariaLabelText}
            target="_blank"
                  rel="noopener noreferrer nofollow"
            // Additional obfuscation: use data attribute instead of direct href
            data-email={encodedEmail}
        >
            {displayText}
        </a>
    );
};

// Wrap component with React.memo to prevent unnecessary re-renders
// Only re-render when props actually change
export const ObfuscatedEmail = React.memo(ObfuscatedEmailComponent);

export default ObfuscatedEmail;

